import type { IChatMessage } from "@/types/(my-space)/chat.interface";

/** A burst of messages chimes once. */
export const CHAT_CHIME_GAP_MS = 1500;

export interface ChimeContext {
  myUserId: number | undefined;
  /** Chats on screen: the Chat page's room and any open chat windows. */
  openConversationIds: ReadonlySet<number>;
  /** The tab is visible and focused: the open chat's messages are seen as they land. */
  isWatching: boolean;
  /** What the cached list/detail says about that chat's mute (undefined: not cached). */
  isMuted: boolean | undefined;
  now: number;
  lastChimeAt: number;
}

/**
 * Whether a new chat message should be announced (popup, sound) — the push's
 * rules: someone else's real message, in a chat I'm not watching, not muted
 * (a tag still gets through a mute, as its push does).
 */
export const shouldNotify = (
  m: IChatMessage,
  ctx: Omit<ChimeContext, "now" | "lastChimeAt">,
) => {
  if (m.type === "system") return false;
  // A channel's notice has no sender, and is exactly what this is for.
  if (m.type !== "notice" && (!m.sender || m.sender.id === ctx.myUserId)) return false;
  if (ctx.isWatching && ctx.openConversationIds.has(m.conversationId)) return false;
  const tagsMe = (m.metadata?.mentions ?? []).some((x) => x.userId === ctx.myUserId);
  return !(ctx.isMuted && !tagsMe);
};

/** shouldNotify, and not right after another chime (a burst rings once). */
export const shouldChime = (m: IChatMessage, ctx: ChimeContext) =>
  shouldNotify(m, ctx) && ctx.now - ctx.lastChimeAt >= CHAT_CHIME_GAP_MS;
